/**
 * What an unconfirmed Sources change tells a person, and what it tells an engineer.
 *
 * TCA-26102604. The frontend has no log channel, so the diagnosis travels in
 * two forms: a five-character code that can be read down a phone, and a JSON
 * object that can be pasted. Both are built here rather than at the call site,
 * so the set of fields that leave the app is one list in one place.
 */

const SUBJECT = { source: 'SO', florist: 'FL', mapping: 'MP', sourceMapping: 'SM' };
const ACTION = { create: 'C', update: 'U', delete: 'D' };

// 200 does not mean it worked. It means the server replied successfully with a
// record that did not match what was sent, which is a different fault from a
// server error and needs to be legible as one.
const CAUSE = { 0: 'no-response', 200: 'server-reply-did-not-match-request' };
const causeFor = status => CAUSE[status] || 'unexpected-server-status';

const operationSegment = (kind) => {
  const [subject, action] = String(kind || '').split(':');
  return `${SUBJECT[subject] || 'XX'}${ACTION[action] || 'X'}`;
};

const statusSegment = status => String(Number.isInteger(status) && status > 0 ? status : 0)
  .padStart(3, '0').slice(-3);

/** `SRC-SOU-U200` — Sources, source update, unconfirmed, server replied 200. */
export const unconfirmedCode = (kind, status) => `SRC-${operationSegment(kind)}-U${statusSegment(status)}`;

// Past this a pasted diagnostic is a wall of text rather than an explanation.
const SIZE_LIMIT = 4096;
const capped = (value) => {
  if (value == null) return null;
  const text = JSON.stringify(value);
  return typeof text === 'string' && text.length > SIZE_LIMIT
    ? { truncated: true, characters: text.length } : value;
};

/**
 * The object behind Copy details.
 *
 * Only the keys listed here leave the app. The session token, request headers,
 * session binding, tenant and user identity are absent by construction rather
 * than by filtering, because a filter has to be remembered and a whitelist does
 * not. `submitted` and `stored_now` are the owner's own catalog data, which is
 * the part that actually explains a mismatch.
 */
export function unconfirmedDiagnostic({
  kind, id, failure, submitted, storedNow, backend, when = new Date().toISOString(),
}) {
  const status = failure?.status;
  return {
    code: unconfirmedCode(kind, status),
    when,
    operation: kind || null,
    record_id: id || null,
    outcome: 'unconfirmed',
    cause: failure?.cause || causeFor(status),
    http_status: Number.isInteger(status) ? status : 0,
    server_detail: typeof failure?.detail === 'string' ? failure.detail : null,
    submitted: capped(submitted ?? null),
    stored_now: capped(storedNow ?? null),
    backend: backend || null,
  };
}
